import clsx from "clsx";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import { GdRichTextInline, GdRichTextParagraphs } from "@/components/ui/GdRichText";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/Button";
import {
  getDokumentTypLabel,
  buildLeistungempfaengerLine,
  buildRecipientAddressLines,
  dokumentDisplayNumber,
  effectiveBelegdatum,
  formatBelegdatum,
  type DocumentChain,
  type DokumentDetail,
} from "@/lib/dokumente";
import { resolveGdPlaceholders } from "@/lib/gdPlaceholders";
import { supplyDateSummary } from "@/lib/leistungsdatum";
import type { HeaderEditSection } from "@/pages/dokumente/DocumentHeaderModal";
import {
  Exc2FrageBlock,
  type Exc2Choice,
} from "@/pages/dokumente/Exc2KalkulationHinweis";
import { InvoiceDraftContextButtons } from "@/pages/dokumente/InvoiceDraftContextButtons";

interface DocumentHeaderSectionProps {
  doc: DokumentDetail;
  editable: boolean;
  importing?: boolean;
  onEditSection: (section: HeaderEditSection) => void;
  onImportFromProject?: () => void;
  /** Dokumentenkette für Rechnungs-Kontextbuttons. */
  chain?: DocumentChain | null;
  /** EXC2-Nachfrage nur bei Angeboten mit Projekt. */
  exc2?: {
    loading: boolean;
    saving: boolean;
    projectIsExc2: boolean;
    choice: Exc2Choice | null;
    onChange: (choice: Exc2Choice) => void;
  } | null;
}

function buildCreatorContactLines(doc: DokumentDetail): string[] {
  const lines: string[] = [];
  if (doc.created_by_name?.trim()) lines.push(doc.created_by_name.trim());
  if (doc.created_by_telefon?.trim()) lines.push(`Telefon: ${doc.created_by_telefon.trim()}`);
  if (doc.created_by_mobil?.trim()) lines.push(`Mobil: ${doc.created_by_mobil.trim()}`);
  if (doc.created_by_email?.trim()) lines.push(`E-Mail: ${doc.created_by_email.trim()}`);
  return lines;
}

function buildProjectContactLines(doc: DokumentDetail): string[] {
  const lines: string[] = [];
  if (doc.ansprechpartner_name?.trim()) lines.push(doc.ansprechpartner_name.trim());
  if (doc.ansprechpartner_telefon?.trim()) {
    lines.push(`Telefon: ${doc.ansprechpartner_telefon.trim()}`);
  }
  if (doc.ansprechpartner_email?.trim()) {
    lines.push(`E-Mail: ${doc.ansprechpartner_email.trim()}`);
  }
  return lines;
}

function HeaderBlock({
  title,
  hint,
  empty,
  editable,
  onClick,
  children,
}: {
  title: string;
  hint?: string;
  empty?: boolean;
  editable: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  const interactive = editable;
  return (
    <button
      type="button"
      className={clsx(
        "gd-header-block w-full rounded-lg border border-separator bg-card p-3 text-left transition",
        interactive && "cursor-pointer hover:border-accent/40 hover:bg-card-elevated",
        empty && "border-dashed",
      )}
      onClick={interactive ? onClick : undefined}
      disabled={!interactive}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-label-secondary">{title}</span>
        {interactive ? (
          <span className="text-xs text-accent">{hint || "Bearbeiten"}</span>
        ) : null}
      </div>
      {children}
    </button>
  );
}

export function DocumentHeaderSection({
  doc,
  editable,
  importing = false,
  onEditSection,
  onImportFromProject,
  chain = null,
  exc2 = null,
}: DocumentHeaderSectionProps) {
  const addressLines = buildRecipientAddressLines(doc);
  const leistungempfaengerLine = buildLeistungempfaengerLine(doc);
  const creatorLines = buildCreatorContactLines(doc);
  const projectContactLines =
    doc.dokument_typ === "rechnung" ? buildProjectContactLines(doc) : [];
  const typLabel = getDokumentTypLabel(doc);
  const nummer = dokumentDisplayNumber(doc);
  const metaItems: string[] = [];
  const belegdatum = effectiveBelegdatum(doc);
  if (belegdatum) metaItems.push(`Belegdatum: ${formatBelegdatum(belegdatum)}`);
  if (doc.dokument_typ === "angebot" && doc.gueltig_bis) {
    metaItems.push(`Gültig bis: ${formatBelegdatum(doc.gueltig_bis)}`);
  }
  const supplyLine = supplyDateSummary(doc, belegdatum);
  if (supplyLine) metaItems.push(supplyLine);
  if (doc.auftrag_nummer) metaItems.push(`Projekt: ${doc.auftrag_nummer}`);
  const bestellnummer = (doc.bestellnummer || doc.auftrag_bestellnummer || "").trim();
  if (bestellnummer) metaItems.push(`Bestellnummer: ${bestellnummer}`);
  const kundenreferenz = (doc.auftrag_kundenreferenz || "").trim();
  if (kundenreferenz) metaItems.push(`Kundenreferenz: ${kundenreferenz}`);
  const leitwegId = (doc.leitweg_id || "").trim();
  if (leitwegId) metaItems.push(`Leitweg-ID: ${leitwegId}`);
  const showExc2 = Boolean(exc2 && doc.dokument_typ === "angebot" && doc.auftrag_id);
  const contactBlockEmpty = creatorLines.length === 0 && projectContactLines.length === 0;

  return (
    <section className="gd-header-section space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-label">Kopfdaten</h2>
          <p className="text-xs text-label-secondary">
            Zum Bearbeiten auf den jeweiligen Bereich klicken.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {doc.dokument_typ === "rechnung" && doc.auftrag_id ? (
            <InvoiceDraftContextButtons
              auftragId={doc.auftrag_id}
              docId={doc.id}
              chain={chain}
            />
          ) : doc.auftrag_id ? (
            <ProjectOverviewButton auftragId={doc.auftrag_id} />
          ) : null}
          {editable && doc.auftrag_id && onImportFromProject ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onImportFromProject}
              loading={importing}
              disabled={importing}
            >
              Aus Projekt übernehmen
            </Button>
          ) : null}
        </div>
      </div>

      {showExc2 && exc2 ? (
        <Exc2FrageBlock
          loading={exc2.loading}
          saving={exc2.saving}
          projectIsExc2={exc2.projectIsExc2}
          choice={exc2.choice}
          editable={editable}
          onChange={exc2.onChange}
        />
      ) : null}

      <div className="gd-header-paper rounded-xl border border-separator bg-card-elevated p-4 shadow-sm">
        <HeaderBlock
          title="Empfänger"
          empty={addressLines.length === 0 && !doc.kundennummer?.trim()}
          editable={editable}
          onClick={() => onEditSection("empfaenger")}
        >
          {addressLines.length || doc.kundennummer?.trim() ? (
            <div className="space-y-0.5 text-sm text-label">
              {addressLines.map((line) => (
                <p key={line}>{line}</p>
              ))}
              {doc.kundennummer?.trim() ? (
                <p className="text-label-secondary">Kundennummer: {doc.kundennummer.trim()}</p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-label-secondary">Keine Adresse — Klicken zum Erfassen</p>
          )}
        </HeaderBlock>
        {leistungempfaengerLine ? (
          <p className="mt-2 px-1 text-sm text-label">{leistungempfaengerLine}</p>
        ) : null}

        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          <HeaderBlock
            title="Betreff & Termine"
            empty={!doc.betreff && metaItems.length === 0}
            editable={editable}
            onClick={() => onEditSection("betreff")}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-label-secondary">{typLabel}</p>
            <p className="text-base font-semibold text-label">{nummer}</p>
            {doc.betreff ? (
              <p className="mt-2 text-sm font-medium text-label">
                <GdRichTextInline text={doc.betreff} />
              </p>
            ) : (
              <p className="mt-2 text-sm text-label-secondary">Kein Betreff</p>
            )}
            {metaItems.length ? (
              <ul className="mt-2 space-y-0.5 text-xs text-label-secondary">
                {metaItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </HeaderBlock>

          <HeaderBlock
            title={
              projectContactLines.length
                ? "Ansprechpartner"
                : "Ansprechpartner (Ersteller)"
            }
            empty={contactBlockEmpty}
            editable={editable}
            onClick={() => onEditSection("ersteller_kontakt")}
          >
            {creatorLines.length ? (
              <div className="space-y-0.5 text-sm text-label">
                {projectContactLines.length ? (
                  <p className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                    Ersteller
                  </p>
                ) : null}
                {creatorLines.map((line) => (
                  <p key={`creator-${line}`}>{line}</p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-label-secondary">
                {projectContactLines.length
                  ? "Kein Ersteller-Kontakt — Klicken zum Erfassen"
                  : "Keine Kontaktdaten — Klicken zum Erfassen"}
              </p>
            )}
            {projectContactLines.length ? (
              <div className="mt-3 space-y-0.5 text-sm text-label">
                <p className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                  Ihr Ansprechpartner
                </p>
                {projectContactLines.map((line) => (
                  <p key={`project-${line}`}>{line}</p>
                ))}
              </div>
            ) : null}
          </HeaderBlock>
        </div>
      </div>
    </section>
  );
}

export function DocumentIntroSection({
  doc,
  editable,
  onEdit,
}: {
  doc: DokumentDetail;
  editable: boolean;
  onEdit: () => void;
}) {
  const display = resolveGdPlaceholders(doc.einleitung, doc);
  return (
    <HeaderBlock
      title="Einleitungstext"
      empty={!doc.einleitung?.trim()}
      editable={editable}
      onClick={onEdit}
      hint="Textbausteine & Platzhalter"
    >
      {display.trim() ? (
        <GdRichTextParagraphs
          text={display}
          className="leading-[1.15]"
          paragraphClassName="m-0 whitespace-pre-wrap text-sm leading-[1.15] text-label"
        />
      ) : (
        <p className="text-sm text-label-secondary">
          Kein Einleitungstext — Klicken zum Erfassen
        </p>
      )}
    </HeaderBlock>
  );
}

export function DocumentFooterSection({
  doc,
  editable,
  onEdit,
}: {
  doc: DokumentDetail;
  editable: boolean;
  onEdit: () => void;
}) {
  const display = resolveGdPlaceholders(doc.schlusstext, doc);
  return (
    <HeaderBlock
      title="Schlusstext"
      empty={!doc.schlusstext?.trim()}
      editable={editable}
      onClick={onEdit}
      hint="Textbausteine & Platzhalter"
    >
      {display.trim() ? (
        <GdRichTextParagraphs
          text={display}
          className="leading-[1.15]"
          paragraphClassName="m-0 whitespace-pre-wrap text-sm leading-[1.15] text-label"
        />
      ) : (
        <p className="text-sm text-label-secondary">Kein Schlusstext</p>
      )}
    </HeaderBlock>
  );
}
